import React from 'react';
import { useAddRiskGap } from './useAddRiskGap';
import { useDeleteSubmissionAnswer } from './useDeleteSubmissionAnswer';
import { useGetGap } from './useGetGap';
import { useGetICDCodes } from './useGetICDCodes';
import { useGetNotes } from './useGetNotes';
import { useGetPatient } from './useGetPatient';
import { useGetStats } from './useGetStats';
import { useLoadPatient } from './useLoadPatient';
import { useUpdatePatient } from './useUpdatePatient';
import { useUpdateFocGap } from './useUpdateFocGap';
import { useUpdateQualityGap } from './useUpdateQualityGap';
import { useRemoveRiskGap } from './useRemoveRiskGap';
import { useRiskGapResponse } from './useRiskGapResponse';
import { useUpdateRiskGap } from './useUpdateRiskGap';
import { useGetHealthHistory } from './useGetHealthHistory';
import { ServicesContext } from '../ServicesContext';

export type PatientCacheRef = React.MutableRefObject<Record<number, PatientType>>;

export const patientServiceEvents = {
    patient_updated: 'PatientService.patientUpdated',
    notes_updated: 'PatientService.notesUpdated',
    reminders_updated: 'PatientService.remindersUpdated',
    uploads_updated: 'PatientService.uploadsUpdated',
    risk_gap_response: 'PatientService.riskGapResponse',
    timely_action_counts_updated: 'PatientService.timelyActionCountsUpdated',
};
export type PatientServiceEvents = typeof patientServiceEvents;

export const usePatientService = () => {
    // Shares the same cache object as the old PatientService class until migration is complete
    const services = React.useContext(ServicesContext);
    const patientCache: PatientCacheRef = React.useRef(services?.patient?.state?.patient ?? {});
    // Keep the ref in sync with the legacy PatientService state
    if (
        services?.patient?.state?.patient &&
        patientCache.current !== services.patient.state.patient
    ) {
        patientCache.current = services.patient.state.patient;
    }

    const addRiskGap = useAddRiskGap(patientServiceEvents, patientCache);
    const getGap = useGetGap(patientServiceEvents, patientCache);
    const deleteSubmissionAnswer = useDeleteSubmissionAnswer(getGap);
    const getICDCodes = useGetICDCodes();
    const getNotes = useGetNotes();
    const getPatient = useGetPatient(patientCache);
    const getStats = useGetStats(patientCache);
    const loadPatient = useLoadPatient(patientServiceEvents, patientCache);
    const updatePatient = useUpdatePatient(patientServiceEvents, patientCache);
    const updateFocGap = useUpdateFocGap(patientServiceEvents, patientCache);
    const updateQualityGap = useUpdateQualityGap(patientServiceEvents, patientCache);
    const removeRiskGap = useRemoveRiskGap(patientServiceEvents, patientCache);
    const riskGapResponse = useRiskGapResponse(patientServiceEvents, patientCache);
    const updateRiskGap = useUpdateRiskGap(patientServiceEvents, patientCache);
    const getHealthHistory = useGetHealthHistory();

    return {
        events: patientServiceEvents,
        addRiskGap,
        deleteSubmissionAnswer,
        getGap,
        getICDCodes,
        getNotes,
        getPatient,
        getStats,
        loadPatient,
        updatePatient,
        removeRiskGap,
        riskGapResponse,
        updateFocGap,
        updateQualityGap,
        updateRiskGap,
        getHealthHistory,
    };
};
